import { useEffect, useRef, useState } from "react";
import Vapi from "@vapi-ai/web";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import ActiveCallDetail from "./components/ActiveCallDetail";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Logo from "./components/logo";

// Initialize Vapi with your Public Key
const vapi = new Vapi(
  process.env.REACT_APP_VAPI_PUBLIC_KEY,
  "https://api.hollo.ai/v1/callbot"
);

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#00adee",
    },
  },
});

const App = () => {
  const [assistants, setAssistants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [connected, setConnected] = useState(false);
  const [callEnabled, setCallEnabled] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(0); // State for volume level
  const [assistantIsSpeaking, setAssistantIsSpeaking] = useState(false); // State for speaking

  const getAssistants = async () => {
    setLoading(true);
    const options = {
      method: "GET",
      headers: { Authorization: `Bearer ${process.env.REACT_APP_VAPI_TOKEN}` },
    };

    fetch("https://api.hollo.ai/v1/callbot/assistant", options)
      .then((response) => response.json())
      .then((response) => {
        const sortedAssistants = response.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setAssistants(sortedAssistants);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getAssistants();
  }, []);

  const handleAssistantSelect = (event, value) => {
    setSelectedAssistant(value);
    setCallEnabled(!!value); // Enable the Call button if an assistant is selected
  };

  const handleCallClick = () => {
    if (selectedAssistant) {
      setConnected(true);
      try {
        vapi.start(selectedAssistant.id);
      } catch (error) {
        console.error("Error starting call with Vapi:", error);
      }
    }
  };

  const handleEndCall = () => {
    setConnected(false);
    vapi.stop();
  };

  useEffect(() => {
    vapi.on("speech-start", () => setAssistantIsSpeaking(true));
    vapi.on("speech-end", () => setAssistantIsSpeaking(false));
    vapi.on("volume-level", (level) => setVolumeLevel(level));
    vapi.on("error", (error) => console.error("Error Emitted:", error));

    return () => {
      vapi.stop();
    };
  }, []);

  // State to track authentication status
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // // Ref to track if the prompt has been shown
  const hasPromptedRef = useRef(false);

  // // Define the correct password (NOT secure for production)
  const CORRECT_PASSWORD = "HolloSkew24!!";

  useEffect(() => {
    // Check if the prompt has already been shown
    if (!hasPromptedRef.current) {
      // Set the ref to true to prevent future prompts
      hasPromptedRef.current = true;

      // Prompt the user for the password when the component mounts
      const password = window.prompt(
        "Please enter the password to view this page:"
      );

      if (password === CORRECT_PASSWORD) {
        // If the password is correct, update the authentication state
        setIsAuthenticated(true);
      } else {
        // If incorrect, alert the user
        window.alert("Incorrect password. You cannot view this page.");
        // Optionally, you can redirect the user or handle access denial here
      }
    }
  }, []); // Empty dependency array ensures this runs once per component lifecycle

  if (!isAuthenticated) {
    return null; // Renders nothing or you can render a fallback UI
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "20px",
        }}
      >
        <div
          style={{
            marginBottom: "30px",
            padding: "10px",
            borderRadius: "5px",
          }}
        >
          <Logo wordColor="white" />
        </div>
        <h1 style={{ fontSize: "24px", color: "white", textAlign: "center" }}>
          {connected
            ? `Calling ${selectedAssistant?.name}`
            : "Select an assistant to start a call"}
        </h1>

        {!connected ? (
          <>
            <Autocomplete
              disablePortal
              options={assistants}
              getOptionLabel={(option) => option.name}
              loading={loading}
              onChange={handleAssistantSelect}
              sx={{ width: 400, marginTop: "20px" }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Choose an assistant"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <Button
              variant="contained"
              color="primary"
              disabled={!callEnabled}
              onClick={handleCallClick}
              fullWidth
              sx={{ width: 400, marginTop: "20px" }}
            >
              Call
            </Button>
          </>
        ) : (
          <ActiveCallDetail
            onEndCallClick={handleEndCall}
            volumeLevel={volumeLevel}
            assistantIsSpeaking={assistantIsSpeaking}
          />
        )}
      </Box>
    </ThemeProvider>
  );
};

export default App;
